import type { ReactNode, RefObject, MouseEvent as ReactMouseEvent } from 'react';
import { useState } from 'react';
import { Popup, FlowForm, getKeyHandler } from 't-lib-ui/structure';
import type { PostRendererHandle } from './PostRenderer';
import DropdownIcon from './DropdownIcon';
import ArrowRightIcon from './ArrowRightIcon';
import HomeIcon from './HomeIcon';
import HamburgerIcon from './HamburgerIcon';
import TwoColumnIcon from './TwoColumnIcon';
import SingleColumnIcon from './SingleColumnIcon';
import ListIcon from './ListIcon';
import SortOrderIcon from './SortOrderIcon';
import UnfollowIcon from './UnfollowIcon';
import FlagIcon from './FlagIcon';
import SkipFeedIcon from './SkipFeedIcon';
import { menuKeyDown, menuBlur } from './menuUtils';
import FeedIcon from './FeedIcon';

interface PostViewHeaderProps {
  // Feed identity
  feedName?: string;
  feedFaviconUrl?: string;
  feedCircularIcon?: boolean;
  privateLockIcon: ReactNode;
  feedNameClass: string;
  feedNameClickHandler?: (ev: ReactMouseEvent) => void;
  // Anon-mode "Log in"/"Sign up" pair, rendered at the start of the header actions.
  // Only passed while no posts are shown (with posts, the actions fill the space).
  authButtons?: ReactNode;

  // Responsive layout
  isMobile?: boolean;
  showExpandButton?: boolean;
  onExpand?: () => void;

  // False when no posts are shown (e.g. the "All caught up" state). Full mode then has no
  // HeadlineList hosting a "Feeds" button, so the medium header falls back to a hamburger.
  hasPosts: boolean;

  // View/sort controls
  viewMode: 'full' | 'single' | 'list';
  onCycleViewMode?: () => void;
  onSetViewMode?: (mode: 'full' | 'single' | 'list') => void;
  sortNewestFirst: boolean;
  onSortChange?: (newest: boolean) => void;
  showRead?: 'u' | 'a';
  onSetShowRead?: (showRead: 'u' | 'a') => void;

  // Mark-read controls
  allMarkedRead: boolean;
  // True when every post of the current feed is read; the header's "Mark all read"
  // button is then replaced by a "Next Feed" (or "Home") button.
  feedAllRead: boolean;
  nextUnreadFeed?: { id: string; name: string };
  onNavigateToFeed?: (feedId: string) => void;
  onMarkAllReadClick: () => void;
  onMarkOlderThanRead: (days: number) => void;
  rendererRef: RefObject<PostRendererHandle | null>;
  showMarkAllHeaderActions: boolean;
  showSmallHeaderActions: boolean;
  // The sort-order toggle only gets a toolbar button at the widest widths (>=1000px);
  // otherwise it's reachable via the options/⋮ menu.
  showSortButton: boolean;

  // Keep-All (mobile ⋮ menu entry) — mirrors the bottom Keep-All button. `keepAllLocked`
  // is true once every post is locked ("Items Locked"); onKeepAll runs the same action.
  keepAllLocked: boolean;
  onKeepAll?: () => void;
  // "Skip feed": keeps every post unread and moves on to the next unread feed (or Home).
  // Shown as an icon-only button at every width. Absent when there is nothing to skip.
  onSkipFeed?: () => void;

  // Undo for the last bulk action (Keep All / Mark All Read). When set, an "Undo" entry is
  // shown at the top of the options/⋮ menu. For a Mark-all-read undo (markAllReadUndoActive)
  // the Skip-feed toolbar button additionally morphs into an Undo button.
  onUndo?: () => void;
  undoLabel?: string;
  markAllReadUndoActive?: boolean;

  // Read-later list specific
  isReadLaterList: boolean;
  hasReadPostsInCurrentList: boolean;
  onRemoveAllReadFromReadLater: () => void;

  // Feed actions
  onLimitUnread?: () => void;
  // The feed's auto-read limits; when set, shows a checkmark on the menu entry and a
  // "max 30/7d" badge next to the feed name that opens the limits dialog.
  feedLimits?: { maxUnread?: number; maxAgeDays?: number };
  // Opens the keyword-filter dialog (pro/vip only — absent hides the menu entry). filterActive
  // marks the menu entry with a checkmark and shows the "filtered" badge next to the feed name.
  onFilterPosts?: () => void;
  filterActive?: boolean;
  onUnfollow?: () => void;
  // Newsletter feeds can't be restored, so the unfollow entry reads "Delete" instead.
  unfollowIsDelete?: boolean;
  onReportFeed?: () => void;
  onReportPost?: () => void;
  specialListType?: 'starred' | 'read-later' | 'all-items' | 'random';
  // Opens the post-appearance dialog (font, size, width)
  onPostAppearance?: () => void;

  // Dropdown open/close state
  showOptionsMenu: boolean;
  setShowOptionsMenu: (v: boolean) => void;
  showMobileMenu: boolean;
  setShowMobileMenu: (v: boolean) => void;
  showMarkReadDropdown: boolean;
  setShowMarkReadDropdown: (v: boolean) => void;

  // Refs for click-outside and focus
  dropdownRef: RefObject<HTMLDivElement | null>;
  mobileMenuRef: RefObject<HTMLDivElement | null>;
  optionsMenuRef: RefObject<HTMLDivElement | null>;
  optionsMenuButtonRef: RefObject<HTMLButtonElement | null>;

  // Flash animation class applied to the options ("⋮") menu button, e.g. to point
  // the user at the menu. Currently unused; kept for ShowMeWhere-style guides.
  whereFlashClass?: string;
}

export default function PostViewHeader({
  feedName, feedFaviconUrl, feedCircularIcon, privateLockIcon, feedNameClass, feedNameClickHandler, authButtons,
  isMobile, showExpandButton, onExpand, hasPosts,
  viewMode, onCycleViewMode, onSetViewMode,
  sortNewestFirst, onSortChange,
  showRead, onSetShowRead,
  allMarkedRead, feedAllRead, nextUnreadFeed, onNavigateToFeed, onMarkAllReadClick, onMarkOlderThanRead, rendererRef,
  showMarkAllHeaderActions, showSmallHeaderActions, showSortButton,
  keepAllLocked, onKeepAll, onSkipFeed,
  onUndo, undoLabel, markAllReadUndoActive,
  isReadLaterList, hasReadPostsInCurrentList, onRemoveAllReadFromReadLater,
  onLimitUnread, feedLimits, onFilterPosts, filterActive, onUnfollow, unfollowIsDelete, onReportFeed, onReportPost, specialListType, onPostAppearance,
  showOptionsMenu, setShowOptionsMenu,
  showMobileMenu, setShowMobileMenu,
  showMarkReadDropdown, setShowMarkReadDropdown,
  dropdownRef, mobileMenuRef, optionsMenuRef, optionsMenuButtonRef,
  whereFlashClass = '',
}: PostViewHeaderProps) {
  const isSavedList = specialListType == 'starred' || specialListType == 'read-later';
  const isRandomFeed = specialListType == 'random';
  // The medium-resolution header (>750px, <=1240px) renders SharedPostViewActions but,
  // unlike desktop, has no room for the "Aa" toolbar button — it lives in the ⋮ menu.
  const isMediumHeader = !isMobile && !!showExpandButton && !!onExpand;

  // At narrow widths the three "Mark read older than …" entries are consolidated into a
  // single menu entry that opens this popup with one button per time frame.
  const [markOlderOpen, setMarkOlderOpen] = useState(false);
  const markOlder = (days: number) => {
    rendererRef.current?.markOlderThanRead(days);
    onMarkOlderThanRead(days);
    setMarkOlderOpen(false);
  };
  const markOlderIcon = (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 4.5V8l2.5 1.5" />
    </svg>
  );

  const undoIcon = (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8a5 5 0 1 1 1.5 3.6" />
      <path d="M3 4.5V8h3.5" />
    </svg>
  );

  const limitUnreadActive = !!(feedLimits?.maxUnread || feedLimits?.maxAgeDays);
  // "max 30/7d" badge next to the feed name; clicking opens the limits dialog.
  const limitsBadge = limitUnreadActive && onLimitUnread ? (
    <button
      className="feedLimitsBadge flowFocussable"
      title="Unread posts are limited — click to change"
      onClick={ev => { ev.stopPropagation(); onLimitUnread(); }}
    >
      max {[
        feedLimits?.maxUnread,
        feedLimits?.maxAgeDays && (feedLimits.maxAgeDays == 1 ? '24h' : `${feedLimits.maxAgeDays}d`),
      ].filter(Boolean).join('/')}
    </button>
  ) : null;

  // "filtered" badge next to the feed name; clicking opens the keyword-filter dialog.
  const filterBadge = filterActive && onFilterPosts ? (
    <button
      className="feedLimitsBadge flowFocussable"
      title="Posts are filtered by keywords — click to change"
      onClick={ev => { ev.stopPropagation(); onFilterPosts(); }}
    >
      filtered
    </button>
  ) : null;

  // Small home button shown next to the hamburger on low/medium-width headers — only on the Unread
  // list, which is usually long and has no other navigation back. It lives inside the clickable
  // header-left area, so stop the click from also toggling the sidebar (the parent's onExpand).
  // Reuses the existing 'home' navigation target.
  const homeButton = onNavigateToFeed && specialListType == 'all-items' ? (
    <button
      className="postViewHomeButton flowFocussable sidewayFocus"
      tabIndex={0}
      title="Home"
      onClick={e => { e.stopPropagation(); onNavigateToFeed('home'); }}
      onKeyDown={getKeyHandler(() => onNavigateToFeed('home'))}
    >
      <HomeIcon size={20} />
    </button>
  ) : null;

  // Icon-only mark-read / next-feed / home button for the mobile header, where there's
  // no room for the labelled markReadGroup. Mirrors the higher-res logic: a checkmark to
  // "Mark all read" while the feed has unread posts, otherwise a Next-Feed arrow (or Home).
  const smallMarkReadButton = !isSavedList && !isRandomFeed ? (
    feedAllRead ? (
      nextUnreadFeed ? (
        <button className="actionButton flowFocussable sidewayFocus" title={`Next feed: ${nextUnreadFeed.name}`} onClick={() => onNavigateToFeed?.(nextUnreadFeed.id)}>
          <ArrowRightIcon size={18} />
        </button>
      ) : (
        <button className="actionButton flowFocussable sidewayFocus" title="Home" onClick={() => onNavigateToFeed?.('home')}>
          <HomeIcon size={18} />
        </button>
      )
    ) : (
      <button className="actionButton flowFocussable sidewayFocus" title="Mark all as read" disabled={allMarkedRead} onClick={onMarkAllReadClick}>
        <span>✓</span>
      </button>
    )
  ) : null;

  // Icon-only "Skip feed" button, shown at every width (mobile included). After "Mark all
  // read" it becomes an Undo button — shown even once the list is empty (unread-tracking
  // feeds clear it), where onSkipFeed is absent.
  const skipFeedButton = !isSavedList && !isRandomFeed ? (
    markAllReadUndoActive && onUndo ? (
      <button className="actionButton skipFeedButton flowFocussable sidewayFocus" title="Undo mark all read" onClick={onUndo}>
        {undoIcon}
      </button>
    ) : onSkipFeed ? (
      <button
        className="actionButton skipFeedButton flowFocussable sidewayFocus"
        title={`Skip feed. Keeps posts unread and goes to ${nextUnreadFeed ? `the next feed: ${nextUnreadFeed.name}` : 'Home'}`}
        onClick={onSkipFeed}
      >
        <SkipFeedIcon size={18} />
      </button>
    ) : null
  ) : null;

  // Shared options dropdown items — identical between medium and desktop variants.
  // Invoked as a plain function call (not <OptionsMenuDropdown/>) so it inlines into
  // the parent's element tree; rendering it as a component defined in this body would
  // give it a new identity each render, remounting the trigger button and dropping
  // focus (e.g. menuKeyDown's Esc-refocus of the ⋮ button).
  const OptionsMenuDropdown = () => (
    <div className="optionsMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowOptionsMenu(false))} onBlur={menuBlur(() => setShowOptionsMenu(false))}>
      {onUndo && <>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onUndo(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">{undoIcon}</span>
        <span className="optionsMenuLabel">{undoLabel || 'Undo'}</span>
      </button>
      <div className="optionsMenuDivider"></div>
      </>}
      {onSetViewMode && <>
      {!isRandomFeed && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('list'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><ListIcon size={16} /></span>
        <span className="optionsMenuLabel">List</span>
        {viewMode == 'list' && <span className="optionsMenuCheck">✓</span>}
      </button>}
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('single'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><SingleColumnIcon size={16} /></span>
        <span className="optionsMenuLabel">Single column</span>
        {viewMode == 'single' && <span className="optionsMenuCheck">✓</span>}
      </button>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('full'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><TwoColumnIcon size={16} /></span>
        <span className="optionsMenuLabel">Full</span>
        {viewMode == 'full' && <span className="optionsMenuCheck">✓</span>}
      </button>
      </>}
      {isMediumHeader && onPostAppearance && <>
      <div className="optionsMenuDivider"></div>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onPostAppearance(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon postAppearanceAa">Aa</span>
        <span className="optionsMenuLabel">Post appearance…</span>
      </button>
      </>}
      {!isRandomFeed && <div className="optionsMenuDivider"></div>}
      {!isRandomFeed && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(true); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">↓</span>
        <span className="optionsMenuLabel">Newest posts first</span>
        {sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
      </button>}
      {!isRandomFeed && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(false); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">↑</span>
        <span className="optionsMenuLabel">Oldest posts first</span>
        {!sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
      </button>}
      {!specialListType && <>
      <div className="optionsMenuDivider"></div>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetShowRead?.('u'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 3.5C6.5 2.3 4.3 2 2.5 2.5v9C4.3 11 6.5 11.3 8 12.5M8 3.5C9.5 2.3 11.7 2 13.5 2.5v9C11.7 11 9.5 11.3 8 12.5M8 3.5v9" />
          </svg>
        </span>
        <span className="optionsMenuLabel">Track unread counts</span>
        {showRead == 'u' && <span className="optionsMenuCheck">✓</span>}
      </button>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetShowRead?.('a'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1.5 8S3.7 3.5 8 3.5 14.5 8 14.5 8 12.3 12.5 8 12.5 1.5 8 1.5 8Z" />
            <circle cx="8" cy="8" r="2" />
          </svg>
        </span>
        <span className="optionsMenuLabel">No unread tracking</span>
        {showRead == 'a' && <span className="optionsMenuCheck">✓</span>}
      </button>
      {onLimitUnread && <div className="optionsMenuDivider"></div>}
      {onLimitUnread && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onLimitUnread(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 3h12M4.5 8h7M6.5 13h3" />
          </svg>
        </span>
        <span className="optionsMenuLabel">Limit unread posts…</span>
        {limitUnreadActive && <span className="optionsMenuCheck">✓</span>}
      </button>}
      {onFilterPosts && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onFilterPosts(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 3h12L9.5 8.5v4.5l-3-1.5V8.5L2 3Z" />
          </svg>
        </span>
        <span className="optionsMenuLabel">Filter posts…</span>
        {filterActive && <span className="optionsMenuCheck">✓</span>}
      </button>}
      </>}
      {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && !feedAllRead && <div className="optionsMenuDivider"></div>}
      {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && isReadLaterList && hasReadPostsInCurrentList && (
        <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onRemoveAllReadFromReadLater(); setShowOptionsMenu(false); }}>
          <span className="optionsMenuIcon">✕</span>
          <span className="optionsMenuLabel">Remove All Read</span>
        </button>
      )}
      {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && !feedAllRead && <button className="optionsMenuItem flowFocussable" tabIndex={0} disabled={allMarkedRead} onClick={() => { onMarkAllReadClick(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">✓</span>
        <span className="optionsMenuLabel">Mark all read</span>
      </button>}
      {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && !feedAllRead && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { setShowOptionsMenu(false); setMarkOlderOpen(true); }}>
        <span className="optionsMenuIcon">{markOlderIcon}</span>
        <span className="optionsMenuLabel">Mark read older than…</span>
      </button>}
      {(onReportPost || onReportFeed || onUnfollow) && <div className="optionsMenuDivider"></div>}
      {onReportPost && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportPost(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
        <span className="optionsMenuLabel">Report this post</span>
      </button>}
      {onReportFeed && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportFeed(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
        <span className="optionsMenuLabel">Report this feed</span>
      </button>}
      {onUnfollow && (
        <button className="optionsMenuItem danger flowFocussable" tabIndex={0} onClick={() => { onUnfollow(); setShowOptionsMenu(false); }}>
          <span className="optionsMenuIcon"><UnfollowIcon size={16} /></span>
          <span className="optionsMenuLabel">{unfollowIsDelete ? 'Delete' : 'Unfollow'}</span>
        </button>
      )}
    </div>
  );

  // Shared action bar — identical between medium and desktop variants. Invoked as a
  // function call (not <SharedPostViewActions/>) so the action buttons keep a stable
  // DOM identity across re-renders; otherwise closing a menu re-renders this body,
  // remounts the buttons, and focus is lost instead of returning to the trigger.
  const SharedPostViewActions = () => (
    <div className="postViewActions">
      {authButtons}
      <div className="optionsMenuContainer" ref={optionsMenuRef}>
        <button ref={optionsMenuButtonRef} className={`actionButton flowFocussable sidewayFocus ${whereFlashClass}`.trim()} onClick={() => setShowOptionsMenu(!showOptionsMenu)} title="Options">
          ⋮
        </button>
        {showOptionsMenu && OptionsMenuDropdown()}
      </div>
      {showSortButton && !isRandomFeed && <button
        className="actionButton sortOrderButton flowFocussable sidewayFocus"
        onClick={() => onSortChange?.(!sortNewestFirst)}
        title={sortNewestFirst ? 'Switch to oldest posts first' : 'Switch to newest posts first'}
      >
        <SortOrderIcon size={72} direction={sortNewestFirst ? 'up' : 'down'} />
      </button>}
      {showSmallHeaderActions && !isMediumHeader && onPostAppearance && (
        <button
          className="actionButton postAppearanceTrigger flowFocussable sidewayFocus"
          title="Post appearance"
          onClick={onPostAppearance}
        >
          <span className="postAppearanceAa">Aa</span>
        </button>
      )}
      {showSmallHeaderActions && onCycleViewMode && (
        <button
          className="actionButton compactToggle flowFocussable sidewayFocus"
          title={viewMode == 'full' ? 'Switch to single-column mode' : viewMode == 'single' ? (isRandomFeed ? 'Switch to full mode' : 'Switch to list mode') : 'Switch to full mode'}
          onClick={() => onCycleViewMode()}
        >
          {viewMode == 'full' ? <TwoColumnIcon size={18} /> : viewMode == 'single' ? <SingleColumnIcon size={18} /> : <ListIcon size={18} />}
        </button>
      )}
      {skipFeedButton}
      {showMarkAllHeaderActions && !isSavedList && !isRandomFeed && isReadLaterList ? (
        <div className="markReadGroup readLaterActions">
          {hasReadPostsInCurrentList && (
            <button className="actionButton markAllRead flowFocussable sidewayFocus" title="Remove read posts from Read Later" onClick={onRemoveAllReadFromReadLater}>
              <span>✕</span>
              <span className="actionButtonLabel">Remove All Read</span>
            </button>
          )}
          <button className="actionButton markAllRead flowFocussable sidewayFocus" title="Mark all as read" disabled={allMarkedRead} onClick={onMarkAllReadClick}>
            <span>✓</span>
            <span className="actionButtonLabel">Mark all read</span>
          </button>
        </div>
      ) : showMarkAllHeaderActions && !isSavedList && !isRandomFeed && feedAllRead ? (
        // Offer the next feed (or Home) instead of a "Mark all read" button when the
        // feed is fully read. Same width as the mark-all button.
        <div className="markReadGroup">
          {nextUnreadFeed ? (
            <button className="actionButton markAllRead headerNavButton flowFocussable sidewayFocus" title={`Next feed: ${nextUnreadFeed.name}`} onClick={() => onNavigateToFeed?.(nextUnreadFeed.id)}>
              <ArrowRightIcon size={14} />
              <span className="actionButtonLabel">Next Feed</span>
            </button>
          ) : (
            <button className="actionButton markAllRead headerNavButton flowFocussable sidewayFocus" title="Home" onClick={() => onNavigateToFeed?.('home')}>
              <HomeIcon size={14} />
              <span className="actionButtonLabel">Home</span>
            </button>
          )}
        </div>
      ) : showMarkAllHeaderActions && !isSavedList && !isRandomFeed ? (
        <div className="markReadGroup" ref={dropdownRef}>
          <button className="actionButton markAllRead flowFocussable sidewayFocus" title="Mark all as read" disabled={allMarkedRead} onClick={onMarkAllReadClick}>
            <span>✓</span>
            <span className="actionButtonLabel">Mark all read</span>
          </button>
          <button
            className="actionButton dropdownToggle flowFocussable sidewayFocus"
            title="Mark read options"
            onClick={() => setShowMarkReadDropdown(!showMarkReadDropdown)}
          >
            <DropdownIcon size={14} />
          </button>
          {showMarkReadDropdown && (
            <div className="dropdownMenu flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowMarkReadDropdown(false))} onBlur={menuBlur(() => setShowMarkReadDropdown(false))}>
              <button className="dropdownItem flowFocussable" tabIndex={0} onClick={() => { rendererRef.current?.markOlderThanRead(1); onMarkOlderThanRead(1); setShowMarkReadDropdown(false); }}>
                Older than one day
              </button>
              <button className="dropdownItem flowFocussable" tabIndex={0} onClick={() => { rendererRef.current?.markOlderThanRead(7); onMarkOlderThanRead(7); setShowMarkReadDropdown(false); }}>
                Older than one week
              </button>
              <button className="dropdownItem flowFocussable" tabIndex={0} onClick={() => { rendererRef.current?.markOlderThanRead(30); onMarkOlderThanRead(30); setShowMarkReadDropdown(false); }}>
                Older than one month
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="postViewHeader">
      {isMobile ? (
        <>
          <div className="mobileHeaderLeft flowFocussable" tabIndex={0} onClick={onExpand}>
            <HamburgerIcon size={24} />
            {homeButton}
            {feedFaviconUrl && <FeedIcon className="feedIcon" src={feedFaviconUrl} size={16} circular={feedCircularIcon} />}
            <h2><span className={feedNameClass} onClick={feedNameClickHandler}>{feedName || 'Unread Posts'}</span>{privateLockIcon}{limitsBadge}{filterBadge}</h2>
          </div>
          <div className="postViewActions">
            {authButtons}
            {showSortButton && !isRandomFeed && <button
              className="actionButton sortOrderButton flowFocussable sidewayFocus"
              onClick={() => onSortChange?.(!sortNewestFirst)}
              title={sortNewestFirst ? 'Switch to oldest posts first' : 'Switch to newest posts first'}
            >
              <SortOrderIcon size={72} direction={sortNewestFirst ? 'up' : 'down'} />
            </button>}
            {showSmallHeaderActions && onCycleViewMode && !isRandomFeed && (
              <button
                className="actionButton compactToggle flowFocussable sidewayFocus"
                title={viewMode == 'list' ? 'Switch to one-column mode' : 'Switch to list mode'}
                onClick={() => onCycleViewMode()}
              >
                {viewMode == 'list' ? <ListIcon size={18} /> : <SingleColumnIcon size={18} />}
              </button>
            )}
            {skipFeedButton}
            {smallMarkReadButton}
            <div className="mobileMenuContainer" ref={mobileMenuRef}>
              <button
                ref={optionsMenuButtonRef}
                className={`mobileMenuButton flowFocussable sidewayFocus ${whereFlashClass}`.trim()}
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                title="Menu"
              >
                ⋮
              </button>
              {showMobileMenu && (
                <div className="mobileMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowMobileMenu(false))} onBlur={menuBlur(() => setShowMobileMenu(false))}>
                {onUndo && <>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onUndo(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">{undoIcon}</span>
                  <span className="optionsMenuLabel">{undoLabel || 'Undo'}</span>
                </button>
                <div className="optionsMenuDivider"></div>
                </>}
                {onSetViewMode && <>
                {!isRandomFeed && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('list'); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><ListIcon size={16} /></span>
                  <span className="optionsMenuLabel">List</span>
                  {viewMode == 'list' && <span className="optionsMenuCheck">✓</span>}
                </button>}
                {/* No HeadlineList at mobile width, so 'full' and 'single' are
                    identical — offer a single "one column" choice (full is shown
                    as single via the effective view mode passed from Main). */}
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('single'); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><SingleColumnIcon size={16} /></span>
                  <span className="optionsMenuLabel">One column</span>
                  {viewMode != 'list' && <span className="optionsMenuCheck">✓</span>}
                </button>
                </>}
                {onPostAppearance && <>
                <div className="optionsMenuDivider"></div>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onPostAppearance(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon postAppearanceAa">Aa</span>
                  <span className="optionsMenuLabel">Post appearance…</span>
                </button>
                </>}
                {/* The mobile header has no toolbar Keep-All button, so offer it in the menu. */}
                {!isSavedList && !isRandomFeed && onKeepAll && <>
                <div className="optionsMenuDivider"></div>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onKeepAll(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={`keepAllMenuIcon${keepAllLocked ? ' locked' : ''}`} aria-hidden="true">
                      <rect x="3" y="5.5" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <path d="M5.5 5.5V3.5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="optionsMenuLabel">{keepAllLocked ? 'Keeping all' : 'Keep all'}</span>
                </button>
                </>}
                {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && !feedAllRead && <div className="optionsMenuDivider"></div>}
                {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && isReadLaterList && hasReadPostsInCurrentList && (
                  <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onRemoveAllReadFromReadLater(); setShowMobileMenu(false); }}>
                    <span className="optionsMenuIcon">✕</span>
                    <span className="optionsMenuLabel">Remove All Read</span>
                  </button>
                )}
                {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && !feedAllRead && <button className="mobileMenuItem flowFocussable" tabIndex={0} disabled={allMarkedRead} onClick={() => { onMarkAllReadClick(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">✓</span>
                  <span className="optionsMenuLabel">Mark all read</span>
                </button>}
                {!showMarkAllHeaderActions && !isSavedList && !isRandomFeed && !feedAllRead && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { setShowMobileMenu(false); setMarkOlderOpen(true); }}>
                  <span className="optionsMenuIcon">{markOlderIcon}</span>
                  <span className="optionsMenuLabel">Mark read older than…</span>
                </button>}
                {!isRandomFeed && <div className="optionsMenuDivider"></div>}
                {!isRandomFeed && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(true); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">↓</span>
                  <span className="optionsMenuLabel">Newest posts first</span>
                  {sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
                </button>}
                {!isRandomFeed && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(false); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">↑</span>
                  <span className="optionsMenuLabel">Oldest posts first</span>
                  {!sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
                </button>}
                {!specialListType && <>
                <div className="optionsMenuDivider"></div>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetShowRead?.('u'); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M8 3.5C6.5 2.3 4.3 2 2.5 2.5v9C4.3 11 6.5 11.3 8 12.5M8 3.5C9.5 2.3 11.7 2 13.5 2.5v9C11.7 11 9.5 11.3 8 12.5M8 3.5v9" />
                    </svg>
                  </span>
                  <span className="optionsMenuLabel">Track unread counts</span>
                  {showRead == 'u' && <span className="optionsMenuCheck">✓</span>}
                </button>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetShowRead?.('a'); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1.5 8S3.7 3.5 8 3.5 14.5 8 14.5 8 12.3 12.5 8 12.5 1.5 8 1.5 8Z" />
                      <circle cx="8" cy="8" r="2" />
                    </svg>
                  </span>
                  <span className="optionsMenuLabel">No unread tracking</span>
                  {showRead == 'a' && <span className="optionsMenuCheck">✓</span>}
                </button>
                {onLimitUnread && <div className="optionsMenuDivider"></div>}
                {onLimitUnread && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onLimitUnread(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2 3h12M4.5 8h7M6.5 13h3" />
                    </svg>
                  </span>
                  <span className="optionsMenuLabel">Limit unread posts…</span>
                  {limitUnreadActive && <span className="optionsMenuCheck">✓</span>}
                </button>}
                {onFilterPosts && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onFilterPosts(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2 3h12L9.5 8.5v4.5l-3-1.5V8.5L2 3Z" />
                    </svg>
                  </span>
                  <span className="optionsMenuLabel">Filter posts…</span>
                  {filterActive && <span className="optionsMenuCheck">✓</span>}
                </button>}
                </>}
                {/* Reporting must stay reachable at every width (DSA Art. 16 notice-and-action),
                    so the mobile menu carries the same report entries as the desktop one. */}
                {(onReportPost || onReportFeed || onUnfollow) && <div className="optionsMenuDivider"></div>}
                {onReportPost && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportPost(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
                  <span className="optionsMenuLabel">Report this post</span>
                </button>}
                {onReportFeed && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportFeed(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
                  <span className="optionsMenuLabel">Report this feed</span>
                </button>}
                {onUnfollow && (
                  <button className="mobileMenuItem danger flowFocussable" tabIndex={0} onClick={() => { onUnfollow(); setShowMobileMenu(false); }}>
                    <span className="optionsMenuIcon"><UnfollowIcon size={16} /></span>
                    <span className="optionsMenuLabel">{unfollowIsDelete ? 'Delete' : 'Unfollow'}</span>
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
        </>
      ) : showExpandButton && onExpand ? (
        <>
          {/* Medium resolution header (>750px, <=1240px) */}
          {viewMode == 'full' && hasPosts ? (
            <h2 className="clickableFeedName flowFocussable" tabIndex={0} onClick={onExpand} onKeyDown={ev => { if (ev.key == 'Enter') { ev.preventDefault(); ev.stopPropagation(); onExpand?.(); } }}>
              {feedFaviconUrl && <FeedIcon className="feedIcon" src={feedFaviconUrl} size={16} circular={feedCircularIcon} />}
              <span className={feedNameClass} onClick={feedNameClickHandler}>{feedName || 'Unread Posts'}</span>{privateLockIcon}{limitsBadge}{filterBadge}
            </h2>
          ) : (
            // Single-column/list — and full mode without posts (the HeadlineList is hidden
            // then) — have no "Feeds" button to reopen the sidebar, so show a hamburger
            // here (matching the mobile header).
            <div className="mediumHeaderLeft flowFocussable" tabIndex={0} onClick={onExpand} onKeyDown={ev => { if (ev.key == 'Enter') { ev.preventDefault(); ev.stopPropagation(); onExpand?.(); } }}>
              <HamburgerIcon size={22} />
              {homeButton}
              {feedFaviconUrl && <FeedIcon className="feedIcon" src={feedFaviconUrl} size={16} circular={feedCircularIcon} />}
              <h2><span className={feedNameClass} onClick={feedNameClickHandler}>{feedName || 'Unread Posts'}</span>{privateLockIcon}{limitsBadge}{filterBadge}</h2>
            </div>
          )}
          {SharedPostViewActions()}
        </>
      ) : (
        <>
          {/* Desktop header (>1240px) */}
          <h2>
            {feedFaviconUrl && <FeedIcon className="feedIcon" src={feedFaviconUrl} size={16} circular={feedCircularIcon} />}
            <span className={feedNameClass} onClick={feedNameClickHandler}>{feedName || 'Unread Posts'}</span>{privateLockIcon}{limitsBadge}{filterBadge}
          </h2>
          {SharedPostViewActions()}
        </>
      )}
      <Popup show={markOlderOpen} title="Mark read older than…" onClose={() => setMarkOlderOpen(false)} closable minWidth="260px" flowForm>
        <FlowForm>
          <div className="markOlderChoices">
            <button className="markOlderChoiceBtn flowFocussable" tabIndex={0} onClick={() => markOlder(1)} onKeyDown={getKeyHandler(() => markOlder(1))}>One day</button>
            <button className="markOlderChoiceBtn flowFocussable" tabIndex={0} onClick={() => markOlder(7)} onKeyDown={getKeyHandler(() => markOlder(7))}>One week</button>
            <button className="markOlderChoiceBtn flowFocussable" tabIndex={0} onClick={() => markOlder(30)} onKeyDown={getKeyHandler(() => markOlder(30))}>One month</button>
          </div>
        </FlowForm>
      </Popup>
    </div>
  );
}
